import { useEffect, useState, cloneElement, ReactElement, RefObject, useMemo } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import SwiperCore, { Navigation, Mousewheel, Lazy } from 'swiper/core';
import isNumber from 'lodash/isNumber';
import styled from 'styled-components';
import 'swiper/swiper-bundle.min.css';

import { IBuyBarProductVariant } from 'shared/types/BuyBarProductVariant';
import { IExternalPicture } from 'shared/types/ExternalPicture';
import { IProductImage } from 'shared/types/ProductImage';
import { ISize } from 'shared/types/Size';
import { ProgressIndicator } from 'shared/components/Carousel/ProgressIndicator';
import { useKeyDown } from 'shared/hooks/useKeyDown';

SwiperCore.use([Navigation, Mousewheel, Lazy]);

interface ISwiperCarouselProps {
  carouselRef?: RefObject<HTMLDivElement>;
  itemList:
    | IExternalPicture[]
    | { mainPictureUrl: string }[]
    | IBuyBarProductVariant[]
    | IProductImage[]
    | ISize[]
    | number[];
  carouselItem: ReactElement;
  disableCarouselScroll?: boolean;
  centeredViewport?: boolean;
  centerOnIndex?: number;
  keyDownIgnoreFocus?: boolean;
  lazyLoad?: boolean;
  allowMissingAsset?: boolean;
  [key: string]: any;
}

export const SwiperCarousel = ({
  carouselRef,
  itemList,
  carouselItem,
  disableCarouselScroll = false,
  centeredViewport,
  centerOnIndex,
  keyDownIgnoreFocus = false,
  lazyLoad = false,
  allowMissingAsset,
  showProgressIndicator = false,
  ...swiperOptions
}: ISwiperCarouselProps) => {
  const [swiper, setSwiper] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const next = () => {
    swiper?.slideNext();
  };

  const previous = () => {
    swiper?.slidePrev();
  };

  useKeyDown('ArrowRight', () => {
    if (
      disableCarouselScroll ||
      (!keyDownIgnoreFocus && document && document.activeElement !== carouselRef?.current)
    ) {
      return;
    }
    next();
  });

  useKeyDown('ArrowLeft', () => {
    if (
      disableCarouselScroll ||
      (!keyDownIgnoreFocus && document && document.activeElement !== carouselRef?.current)
    ) {
      return;
    }
    previous();
  });

  const slideToIndex = (idx) => {
    if (isNumber(idx) && idx >= 0) {
      if (swiper?.loopedSlides > 0) {
        swiper?.slideToLoop(idx, 300);
      } else {
        swiper?.slideTo(idx, 300);
      }
    }
  };

  const handleSlideChange = (swipercore) => {
    const { activeIndex: idx } = swipercore;
    setActiveIndex(idx);
  };

  const preloadNext = () => {
    const nextIndex = (swiper.activeIndex as number) + 1;
    if (nextIndex < swiper.slides?.length) {
      const nextSlide = swiper.slides?.[nextIndex];
      const imgElement = nextSlide?.querySelector('img');

      if (imgElement) {
        imgElement.setAttribute('loading', 'eager');
      }
    }
  };

  useEffect(() => {
    if (centerOnIndex >= 0 && itemList.length > 0) {
      slideToIndex(centerOnIndex);
    }
  }, [swiper, itemList, centerOnIndex]);

  useEffect(() => {
    // for lazy loading, we want to load the next image so it's ready when we swipe to it
    if (lazyLoad && swiper) {
      preloadNext();
      swiper.on('slideChange', preloadNext);
    }
  }, [lazyLoad, swiper]);

  useEffect(() => {
    // reset back to the first index when the items change
    slideToIndex(0);
  }, [JSON.stringify(itemList)]);

  // No items to display. Trigger placeholder.
  const items =
    itemList?.length === 0 && allowMissingAsset ? [{ mainPictureUrl: 'missing.png' }] : itemList;

  const memoizedCarouselItem = useMemo(
    () =>
      items?.map((item, idx) => (
        // eslint-disable-next-line react/no-array-index-key -- sometimes mainPictureUrl is the only key in the itemList, and it may not be unique, so using idx
        <SwiperSlide key={idx}>
          {cloneElement(carouselItem, { item, itemList, idx, centeredViewport, activeIndex })}
        </SwiperSlide>
      )),
    [items],
  );

  return (
    <Swiper
      id="main"
      data-qa="swiper_carousel"
      onSwiper={setSwiper}
      onSlideChange={swiperOptions.onSlideChange || handleSlideChange}
      lazy={lazyLoad}
      {...swiperOptions}
    >
      {memoizedCarouselItem}
      {showProgressIndicator && (
        <SwiperCarousel.ProgressIndicator
          activeIndex={activeIndex + 1}
          itemListLength={itemList?.length}
        />
      )}
    </Swiper>
  );
};

SwiperCarousel.ProgressIndicator = styled(ProgressIndicator)`
  text-align: center;
`;
